import { useDispatch, useSelector, useTranslation } from '@spcc/core'
import { Badge, Button, cn } from '@spcc/uikit'
import { Calendar, Lock, X } from '@spcc/uikit'
import type { DateRange, SubCustomerAudience } from '../../../domain/types'
import { AUDIENCE_CONFIG, DATE_PRESETS, SUB_CUSTOMER_T_KEYS as K } from '../../../domain/constants'
import { mergeFilters } from '../../../domain/utils/sub-customer-dashboard'
import { subCustomerDashboardActions as A } from '../../../store/sub-customer-dashboard.slice'
import {
  selectSubCustomerDateRange,
  selectSubCustomerFilterChips,
  selectSubCustomerFilters,
  selectSubCustomerGlobalFactoryIds,
  selectSubCustomerGlobalPlantLabel,
  selectSubCustomerGlobalTenant,
} from '../../../store/sub-customer-dashboard.selectors'

const GROUP_LABEL = { customerIds: K.MAIN_CUSTOMER, cityIds: K.CITY, variantIds: K.CEMENT_TYPE } as const
const CHIP = 'h-8 gap-1 rounded-full px-3 text-xs font-normal'
/** Removable chips are buttons: 44px tall on phones. */
const CHIP_BUTTON = cn(CHIP, 'h-11 sm:h-8')

function dateLabel(range: DateRange, t: (key: string) => string): string {
  if (range.preset === 'custom' && range.from && range.to) return `${range.from} → ${range.to}`
  return t(DATE_PRESETS[range.preset].labelKey)
}

function LockedChip({ label, value }: { label: string; value: string }) {
  return (
    <Badge variant="outline" className={CHIP}>
      <Lock className="h-3 w-3" />
      <span className="text-muted-foreground">{label}:</span> {value}
    </Badge>
  )
}

/** What the page is showing right now: the window, the header scope (locked) and each picked filter (removable). */
export function ActiveFilterChips({ audience }: { audience: SubCustomerAudience }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const dateRange = useSelector(selectSubCustomerDateRange)
  const filters = useSelector(selectSubCustomerFilters)
  const chips = useSelector(selectSubCustomerFilterChips)
  const headerTenant = useSelector(selectSubCustomerGlobalTenant)
  const headerFactoryIds = useSelector(selectSubCustomerGlobalFactoryIds)
  const headerPlant = useSelector(selectSubCustomerGlobalPlantLabel)
  const remove = (group: keyof typeof GROUP_LABEL, id: string) =>
    dispatch(A.setFilters(mergeFilters(filters, { [group]: (filters[group] ?? []).filter((value) => value !== id) })))

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant="secondary" className={CHIP}>
        <Calendar className="h-3 w-3" />{dateLabel(dateRange, t)}
      </Badge>
      {headerFactoryIds && <LockedChip label={t(K.PLANT)} value={headerPlant ?? t(K.LOCKED_HINT)} />}
      {AUDIENCE_CONFIG[audience].showMainCustomer && headerTenant && <LockedChip label={t(K.MAIN_CUSTOMER)} value={headerTenant.name} />}
      {chips.map((chip) => (
        <Button key={`${chip.group}-${chip.id}`} variant="secondary" size="sm" className={CHIP_BUTTON} onClick={() => remove(chip.group, chip.id)}>
          <span className="text-muted-foreground">{t(GROUP_LABEL[chip.group])}:</span>
          <span className="max-w-[10rem] truncate">{chip.label}</span>
          <X className="ms-1 h-3 w-3" />
        </Button>
      ))}
      {chips.length > 1 && (
        <Button variant="ghost" size="sm" className="h-11 text-xs text-muted-foreground sm:h-8" onClick={() => dispatch(A.setFilters({}))}>
          {t(K.FILTERS.CLEAR_ALL)}
        </Button>
      )}
    </div>
  )
}
